import type { ExperimentRecord } from './experimentRecordService';

const API_BASE = '/api';

const getToken = () => localStorage.getItem('token');

async function apiRequest(url: string, options: RequestInit = {}) {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {})
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}${url}`, { ...options, headers });

  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
      throw new Error('未授权，请重新登录');
    }
    const err = await response.json().catch(() => ({ message: '请求失败' }));
    throw new Error(err.message || err.error || '请求失败');
  }

  return response.json();
}

export interface SharedExperimentNotebookPermissions {
  canAddRecords: boolean;
  canRemoveRecords: boolean;
  canViewUsers: boolean;
  /** 共享成员是否可编辑夹内他人创建的实验记录 */
  canEditRecords?: boolean;
}

export interface SharedExperimentNotebookUser {
  user: { _id: string; username?: string; email?: string };
  email: string;
  sharedAt?: string;
  status?: 'pending' | 'joined';
}

export interface SharedExperimentNotebookFolder {
  _id: string;
  name: string;
  description?: string;
  owner: { _id: string; username?: string; email?: string };
  sharedUsers: SharedExperimentNotebookUser[];
  isPublic?: boolean;
  permissions?: SharedExperimentNotebookPermissions;
  createdAt: string;
  updatedAt: string;
}

export interface SharedExperimentNotebookItem {
  _id: string;
  folder: string;
  experimentRecord: ExperimentRecord | string;
  addedBy: { _id: string; username?: string; name?: string; email?: string };
  snapshot?: {
    title?: string;
    summary?: string;
    discipline?: string;
    status?: string;
    updatedAt?: string;
  };
  createdAt: string;
}

export const sharedExperimentNotebookService = {
  getMyFolders: async (): Promise<SharedExperimentNotebookFolder[]> => {
    const res = await apiRequest('/shared/experiment-notebooks/folders');
    return res.data.folders as SharedExperimentNotebookFolder[];
  },

  getSharedWithMeFolders: async (): Promise<SharedExperimentNotebookFolder[]> => {
    const res = await apiRequest('/shared/experiment-notebooks/folders/shared-with-me');
    return res.data.folders as SharedExperimentNotebookFolder[];
  },

  createFolder: async (data: { name: string; description?: string }): Promise<SharedExperimentNotebookFolder> => {
    const res = await apiRequest('/shared/experiment-notebooks/folders', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return res.data.folder as SharedExperimentNotebookFolder;
  },

  updateFolder: async (
    id: string,
    data: { name?: string; description?: string; permissions?: Partial<SharedExperimentNotebookPermissions> }
  ): Promise<SharedExperimentNotebookFolder> => {
    const res = await apiRequest(`/shared/experiment-notebooks/folders/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return res.data.folder as SharedExperimentNotebookFolder;
  },

  deleteFolder: async (id: string): Promise<void> => {
    await apiRequest(`/shared/experiment-notebooks/folders/${id}`, { method: 'DELETE' });
  },

  inviteFolderUser: async (folderId: string, email: string, message?: string): Promise<void> => {
    await apiRequest(`/shared/experiment-notebooks/folders/${folderId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, message: message || '' })
    });
  },

  addFolderUser: async (folderId: string, email: string): Promise<SharedExperimentNotebookFolder> => {
    const res = await apiRequest(`/shared/experiment-notebooks/folders/${folderId}/users`, {
      method: 'POST',
      body: JSON.stringify({ email })
    });
    return res.data.folder as SharedExperimentNotebookFolder;
  },

  removeFolderUser: async (folderId: string, userId: string): Promise<SharedExperimentNotebookFolder> => {
    const res = await apiRequest(`/shared/experiment-notebooks/folders/${folderId}/users/${userId}`, {
      method: 'DELETE'
    });
    return res.data.folder as SharedExperimentNotebookFolder;
  },

  getFolderRecords: async (
    folderId: string
  ): Promise<{ folder: SharedExperimentNotebookFolder; items: SharedExperimentNotebookItem[] }> => {
    const res = await apiRequest(`/shared/experiment-notebooks/folders/${folderId}/records`);
    return {
      folder: res.data.folder as SharedExperimentNotebookFolder,
      items: res.data.items as SharedExperimentNotebookItem[]
    };
  },

  addRecordsToFolder: async (
    folderId: string,
    recordIds: string[]
  ): Promise<{ addedCount: number; items: SharedExperimentNotebookItem[] }> => {
    const res = await apiRequest(`/shared/experiment-notebooks/folders/${folderId}/records`, {
      method: 'POST',
      body: JSON.stringify({ recordIds })
    });
    return {
      addedCount: res.data.addedCount as number,
      items: res.data.items as SharedExperimentNotebookItem[]
    };
  },

  removeRecordFromFolder: async (folderId: string, recordId: string): Promise<void> => {
    await apiRequest(`/shared/experiment-notebooks/folders/${folderId}/records/${recordId}`, {
      method: 'DELETE'
    });
  }
};
